import { darken, lighten, shade } from "polished";
import { black, white } from "../colors";
import { brandPrimary, brandSecondary } from "../colors/brand";
import * as steelGrey from "../colors/steelGrey";
import * as deepOrange from "../colors/deepOrange";
import * as deepBlue from "../colors/deepBlue";
import * as red from "../colors/red";
import * as yellow from "../colors/yellow";
import * as lightGreen from "../colors/lightGreen";
import * as green from "../colors/green";
import * as lime from "../colors/lime";
import * as amber from "../colors/amber";
import * as aqua from "../colors/aqua";
import * as blue from "../colors/blue";
import * as cyan from "../colors/cyan";
import * as teal from "../colors/teal";
import * as orange from "../colors/orange";
import * as pink from "../colors/pink";
import * as lightBlue from "../colors/lightBlue";
import * as lightPurple from "../colors/lightPurple";
import * as deepPink from "../colors/deepPink";
import * as purple from "../colors/purple";
import * as deepPurple from "../colors/deepPurple";
export const alchemyDark = {
    // Base (structural)
    structural: {
        base: steelGrey.steelGreyA400,
        inverse: black,
        contrast: white,
        highlight: steelGrey.steelGrey800,
        hint: steelGrey.steelGrey900,
        // Brand colors
        brandPrimary,
        brandSecondary,
        // Text colors
        typeLink: lightBlue.lightBlue300,
        typeHeader: white,
        typeSubHeader: lighten(0.05, steelGrey.steelGrey300),
        typeBody: steelGrey.steelGrey200,
        typeMeta: lighten(0.05, steelGrey.steelGrey300),
        // UI
        line: steelGrey.steelGrey800,
        lineContrast: steelGrey.steelGrey700,
        inputBorder: lighten(0.02, steelGrey.steelGrey300),
        fill: steelGrey.steelGrey900,
        canvas: shade(0.16, steelGrey.steelGrey900),
        container: steelGrey.steelGrey1000
    },
    // Interactive
    interactiveNavigational: {
        contrast: steelGrey.steelGrey200,
        base: steelGrey.steelGrey500,
        highlight: darken(0.02, steelGrey.steelGrey900),
        hint: darken(0.06, steelGrey.steelGrey900)
    },
    interactiveConstructive: {
        contrast: deepBlue.deepBlue200,
        base: lighten(0.04, deepBlue.deepBlue500),
        highlight: darken(0.22, deepBlue.deepBlue500),
        hint: deepBlue.deepBlueA1000
    },
    interactiveDestructive: {
        contrast: red.red200,
        base: red.redA400,
        highlight: darken(0.22, red.redA400),
        hint: darken(0.34, red.redA400)
    },
    interactiveAction: {
        contrast: darken(0.02, green.green1000),
        base: green.greenA500,
        highlight: darken(0.22, green.greenA400),
        hint: darken(0.3, green.greenA400)
    },
    // Expressive
    expressivePositive: {
        contrast: green.green200,
        base: green.greenA400,
        a11yBase: darken(0.091, green.greenA400),
        gradient: green.green600,
        highlight: darken(0.22, green.greenA400),
        hint: darken(0.3, green.greenA400)
    },
    expressiveSemiPositive: {
        contrast: lime.lime200,
        base: lime.limeA400,
        a11yBase: lime.limeA1000,
        gradient: lime.lime500,
        highlight: darken(0.22, lime.limeA400),
        hint: darken(0.34, lime.limeA400)
    },
    expressiveNegative: {
        contrast: red.red200,
        base: lighten(0.12, red.redA400),
        a11yBase: lighten(0.047, red.redA400),
        gradient: red.red500,
        highlight: darken(0.22, red.redA400),
        hint: darken(0.34, red.redA400)
    },
    expressiveSemiNegative: {
        contrast: deepOrange.deepOrange200,
        base: deepOrange.deepOrange500,
        a11yBase: darken(0.022, deepOrange.deepOrange1000),
        gradient: deepOrange.deepOrange400,
        highlight: darken(0.34, deepOrange.deepOrange500),
        hint: darken(0.44, deepOrange.deepOrange500)
    },
    expressiveWarning: {
        contrast: yellow.yellow200,
        base: yellow.yellowA400,
        a11yBase: lighten(0.11, yellow.yellowA1000),
        gradient: yellow.yellow500,
        highlight: darken(0.34, yellow.yellowA400),
        hint: darken(0.44, yellow.yellowA400)
    },
    expressiveInformative: {
        contrast: deepBlue.deepBlue200,
        base: lighten(0.04, deepBlue.deepBlue500),
        a11yBase: deepBlue.deepBlue500,
        gradient: deepBlue.deepBlue500,
        highlight: darken(0.22, deepBlue.deepBlue500),
        hint: darken(0.34, deepBlue.deepBlue500)
    },
    expressiveNeutral: {
        contrast: steelGrey.steelGrey200,
        base: lighten(0.08, steelGrey.steelGrey500),
        a11yBase: steelGrey.steelGrey500,
        gradient: lighten(0.08, steelGrey.steelGrey400),
        highlight: darken(0.02, steelGrey.steelGrey900),
        hint: darken(0.06, steelGrey.steelGrey900)
    },
    // Illustrative
    illustrativePurple: {
        a11yBase: purple.purple600,
        base: purple.purple500,
        contrast: purple.purple800,
        highlight: purple.purple200,
        hint: purple.purple50
    },
    illustrativeBlue: {
        a11yBase: blue.blue700,
        base: blue.blue500,
        contrast: blue.blue800,
        highlight: blue.blue200,
        hint: blue.blue50
    },
    illustrativeLightBlue: {
        a11yBase: lightBlue.lightBlue800,
        base: lightBlue.lightBlue500,
        contrast: lightBlue.lightBlue900,
        highlight: lightBlue.lightBlue200,
        hint: lightBlue.lightBlue50
    },
    illustrativeCyan: {
        a11yBase: cyan.cyan800,
        base: cyan.cyan500,
        contrast: cyan.cyan900,
        highlight: cyan.cyan200,
        hint: cyan.cyan50
    },
    illustrativeAqua: {
        a11yBase: darken(0.11, aqua.aqua900),
        base: aqua.aqua500,
        contrast: aqua.aqua1000,
        highlight: aqua.aqua200,
        hint: aqua.aqua50
    },
    illustrativeTeal: {
        a11yBase: darken(0.017, teal.teal900),
        base: teal.teal500,
        contrast: teal.teal900,
        highlight: teal.teal200,
        hint: teal.teal50
    },
    illustrativeGreen: {
        a11yBase: green.green1000,
        base: green.green500,
        contrast: green.green900,
        highlight: green.green200,
        hint: green.green50
    },
    illustrativeLightGreen: {
        a11yBase: darken(0.091, lightGreen.lightGreen900),
        base: lightGreen.lightGreen500,
        contrast: lightGreen.lightGreen900,
        highlight: lightGreen.lightGreen200,
        hint: lightGreen.lightGreen50
    },
    illustrativeLime: {
        a11yBase: darken(0.061, lime.lime1000),
        base: lime.lime500,
        contrast: lime.lime1000,
        highlight: lime.lime200,
        hint: lime.lime50
    },
    illustrativeYellow: {
        a11yBase: darken(0.36, yellow.yellow500),
        base: yellow.yellow500,
        contrast: yellow.yellowA500,
        highlight: yellow.yellow200,
        hint: yellow.yellow50
    },
    illustrativeOrange: {
        a11yBase: darken(0.17, orange.orangeA400),
        base: orange.orange500,
        contrast: orange.orangeA400,
        highlight: orange.orange200,
        hint: orange.orange50
    },
    illustrativeAmber: {
        a11yBase: amber.amber900,
        base: amber.amber500,
        contrast: amber.amber700,
        highlight: amber.amber200,
        hint: amber.amber50
    },
    illustrativeDeepOrange: {
        a11yBase: deepOrange.deepOrange800,
        base: deepOrange.deepOrange500,
        contrast: deepOrange.deepOrange700,
        highlight: deepOrange.deepOrange200,
        hint: deepOrange.deepOrange50
    },
    illustrativeRed: {
        a11yBase: red.red700,
        base: red.red500,
        contrast: red.red600,
        highlight: red.red200,
        hint: red.red50
    },
    illustrativePink: {
        a11yBase: pink.pink900,
        contrast: pink.pink700,
        base: pink.pink500,
        highlight: pink.pink200,
        hint: pink.pink50
    },
    illustrativeDeepPink: {
        a11yBase: deepPink.deepPink800,
        contrast: deepPink.deepPink900,
        base: deepPink.deepPink500,
        highlight: deepPink.deepPink200,
        hint: deepPink.deepPink50
    },
    illustrativeLightPurple: {
        a11yBase: lightPurple.lightPurple700,
        base: lightPurple.lightPurple500,
        contrast: lightPurple.lightPurple800,
        highlight: lightPurple.lightPurple200,
        hint: lightPurple.lightPurple50
    },
    illustrativeDeepPurple: {
        a11yBase: deepPurple.deepPurple600,
        base: deepPurple.deepPurple500,
        contrast: deepPurple.deepPurple800,
        highlight: deepPurple.deepPurple200,
        hint: deepPurple.deepPurple50
    },
    illustrativeDeepBlue: {
        a11yBase: deepBlue.deepBlue600,
        base: deepBlue.deepBlue500,
        contrast: deepBlue.deepBlue800,
        highlight: deepBlue.deepBlue100,
        hint: deepBlue.deepBlue50
    },
    illustrativeSteelGrey: {
        a11yBase: steelGrey.steelGrey700,
        base: steelGrey.steelGrey300,
        contrast: steelGrey.steelGrey900,
        highlight: steelGrey.steelGrey200,
        hint: steelGrey.steelGrey50
    },
    illustrativeBrand: {
        a11yBase: darken(0.02, purple.purple500),
        base: brandPrimary,
        contrast: darken(0.2, purple.purple500),
        highlight: lighten(0.25, purple.purple500),
        hint: lighten(0.34, purple.purple500)
    },
    visualizationOne: {
        contrast: purple.purpleA1000,
        gradient: purple.purple600,
        base: purple.purple700,
        highlight: purple.purple200,
        hint: purple.purple50,
        inverseContainer: purple.purple600
    },
    visualizationTwo: {
        contrast: lightBlue.lightBlueA1000,
        gradient: lightBlue.lightBlue400,
        base: lightBlue.lightBlue500,
        highlight: lightBlue.lightBlue200,
        hint: lightBlue.lightBlue50
    },
    visualizationThree: {
        contrast: aqua.aquaA1000,
        gradient: aqua.aqua300,
        base: aqua.aqua400,
        highlight: aqua.aqua200,
        hint: aqua.aqua50,
        inverseContainer: aqua.aqua900
    },
    visualizationFour: {
        contrast: blue.blueA1000,
        base: lightBlue.lightBlue700,
        gradient: lightBlue.lightBlue600,
        highlight: blue.blue200,
        hint: blue.blue50,
        inverseContainer: blue.blue600
    },
    visualizationFive: {
        contrast: cyan.cyanA1000,
        gradient: teal.teal800,
        base: teal.teal900,
        highlight: cyan.cyan200,
        hint: cyan.cyan50,
        inverseContainer: cyan.cyan700
    },
    visualizationSix: {
        contrast: teal.tealA1000,
        gradient: teal.teal500,
        base: teal.teal600,
        highlight: teal.teal200,
        hint: teal.teal50,
        inverseContainer: teal.teal800
    },
    visualizationSeven: {
        contrast: green.greenA1000,
        base: green.green400,
        gradient: green.green300,
        highlight: green.green200,
        hint: green.green50
    },
    visualizationEight: {
        contrast: lightGreen.lightGreenA1000,
        gradient: green.greenA400,
        base: green.greenA500,
        highlight: lightGreen.lightGreen200,
        hint: lightGreen.lightGreen50
    },
    visualizationNine: {
        contrast: lime.limeA1000,
        base: lime.lime600,
        gradient: lime.lime500,
        highlight: lime.lime200,
        hint: lime.lime50
    },
    // Sentiment
    sentimentInformative: {
        contrast: deepBlue.deepBlue50,
        base: deepBlue.deepBlue500,
        highlight: darken(0.1, deepBlue.deepBlue500),
        hint: darken(0.34, deepBlue.deepBlue500)
    },
    sentimentNegative: {
        contrast: red.red50,
        base: lighten(0.12, red.redA400),
        highlight: darken(0.1, red.redA400),
        hint: darken(0.34, red.redA400)
    },
    sentimentNeutral: {
        contrast: steelGrey.steelGrey50,
        base: lighten(0.08, steelGrey.steelGrey500),
        highlight: darken(0.1, steelGrey.steelGrey500),
        hint: darken(0.3, steelGrey.steelGrey500)
    },
    sentimentPositive: {
        contrast: green.green50,
        base: green.greenA400,
        highlight: darken(0.1, green.greenA400),
        hint: darken(0.3, green.greenA400)
    },
    sentimentSemiNegative: {
        contrast: deepOrange.deepOrange50,
        base: deepOrange.deepOrange500,
        highlight: darken(0.12, deepOrange.deepOrange500),
        hint: darken(0.44, deepOrange.deepOrange500)
    },
    sentimentSemiPositive: {
        contrast: lime.lime50,
        base: lime.limeA400,
        highlight: darken(0.1, lime.limeA400),
        hint: darken(0.34, lime.limeA400)
    },
    sentimentWarning: {
        contrast: yellow.yellow50,
        base: yellow.yellowA400,
        highlight: darken(0.1, yellow.yellowA400),
        hint: darken(0.44, yellow.yellowA400)
    }
};
